import React, { useEffect, useMemo, useState } from 'react'
import BaseModal from './Base'
import SignInModal from './SignIn'
import type { PrbCoupon } from 'mobx/Coupons'
import { inject, observer } from 'mobx-react'
import { useStores } from 'hooks/useStores'
import type CouponError from 'mobx/Coupons/errors'
import type _Account from 'mobx/Account'
import type _Application from 'mobx/Application'
import { sendCustomEvent } from 'utils/analytics/analytics'
import ImageNext from 'components/common/ImageNext'
import CouponCodeSkeleton from '../CouponCodeSkeleton'
import type { LanguageLocale } from 'utils/language'
import { getTranslatedTextByKey } from 'utils/language'
import Alert from 'components/common/Alert'
import styled from 'styled-components'
import CopyCode from '../CopyCode'
import { ActionType } from './utils'
import type { ExternalCoupon } from 'shared-types/Coupon'
import CouponsStore from 'mobx/Coupons'

import useAuthenticator from 'hooks/useAuthenticator'
import QRCodeCanvas from 'components/common/QRCode'
import { Qr as QrSkeleton } from '../CouponCodeSkeleton'
import { QRCodeType } from 'utils/constants'

interface Props {
	coupon: ExternalCoupon
	signIn: boolean
	locale: LanguageLocale
	error?: string
	onClose?: () => void
}

const QrContainer = styled.div`
	position: relative;
	width: 45%;
	aspect-ratio: 1 / 1;
`

const Section = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`

const CodeAndTip = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.5rem;
`

const Line = styled.div`
	width: 100%;
	min-height: 2px;

	background-image: linear-gradient(to right, var(--disable) 50%, var(--clear) 0%);
	background-position: top;
	background-size: 0.75em 1px;
	background-repeat: repeat;
`

const RequireLoginExternalCouponQrCode = ({rawCode, authCode}: {rawCode: string ; authCode: string}) => {
	return (
		authCode ? (
			<QRCodeCanvas content={{type: QRCodeType.RequireLoginExternalCoupon, data: {rawCode, authCode}}} />
		) : (
			<QrSkeleton />
		)
	)
}
	

const CodeAndQr: React.FC<{ id: string; onError: (error: CouponError) => void }> = inject('Account', 'Application')(
	observer(({ id, onError, Account, Application }: { id: string; Account: typeof _Account; Application: typeof _Application; onError: (error: CouponError) => void }) => {
		const [codeAndQr, setCodeAndQr] = useState<PrbCoupon | null>(null)
		const { couponsStore } = useStores()

		const { 
			start: startAuthenticator, 
			connectionState: authenticatorState, 
			isConnected: authenticatorIsConnected 
		} = useAuthenticator(Application, Account, () => {
			startAuthenticator()
		})

		useEffect(() => {
			startAuthenticator()
		}, [])

		useEffect(() => {
			couponsStore
				.getPrbCode(id)
				.then(setCodeAndQr)
				.catch((error: CouponError) => {
					setCodeAndQr(null)
					onError(error)
				})

			sendCustomEvent({
				category: 'debug_instore',
				action: 'get_prb_code',
				couponId: id,
				user: Account?.user?.user?.email || '',
				userFirstName: Account?.user?.user?.firstName || '',
				time: new Date().toISOString(),
				page_path: window.location.pathname,
			})
		}, [Account?.user?.user?.email, Account?.user?.user?.firstName, couponsStore, id, onError])

		return (
			<>
				<Line />
				<CodeAndTip>
					<Alert variant="info" message={getTranslatedTextByKey('eCommerce.coupons.modal.info', 'Show at the cash register')} />
					<Section>{codeAndQr?.code ? <CopyCode code={codeAndQr.code} /> : <CouponCodeSkeleton showCode />}</Section>
				</CodeAndTip>
				{codeAndQr ? (
					codeAndQr.data && !authenticatorIsConnected ? (
						<RequireLoginExternalCouponQrCode rawCode={codeAndQr.data} authCode={authenticatorState.code} />
					) : (
						<QrContainer>
							<ImageNext fallbackSrc="" src={codeAndQr.url} alt="QR Code" fill />
						</QrContainer>
					)
				) : (
					<CouponCodeSkeleton showQr />
				)}
			</>
		)
	})
)

const ExternalModal: React.FC<Props> = ({ coupon, locale, error: parentError, signIn, onClose }) => {
	const [error, setError] = useState<CouponError | null>(null)
	const localizedCoupon = useMemo(() => CouponsStore.localizeCoupon(coupon, locale), [coupon, locale])

	return signIn ? (
		<SignInModal save={false} coupon={coupon} locale={locale} onError={() => {}} actionType={ActionType.View} onClose={onClose} />
	) : (
		<BaseModal
			coupon={localizedCoupon}
			error={parentError ?? error?.text()}
			content={<CodeAndQr id={coupon.id} onError={setError} />}
			onClose={onClose}
		/>
	)
}

export default ExternalModal
